import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { motion } from 'framer-motion';
import { Church, Upload, Save, Heart, QrCode, Landmark, ExternalLink } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { createPageUrl } from '@/utils';
import { optimizeBannerImage } from '@/lib/imageCompression';

export default function AdminIgreja() {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    history: '',
    mission: '',
    vision: '',
    values: '',
    cover_image_url: '',
    address: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    instagram: '',
    facebook: '',
    youtube_channel: '',
    youtube_live_url: '',
    youtube_videos_feed_url: '',
    latitude: '',
    longitude: '',
    donation_title: '',
    donation_text: '',
    donation_image_url: '',
    pix_qr_image_url: '',
    pix_key: '',
    pix_copy_link: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
    bank_account_type: '',
    bank_beneficiary: '',
    bank_document: '',
    footer_logo_url: '',
    footer_link_1_label: '',
    footer_link_2_label: '',
    footer_link_3_label: '',
    footer_contact_phone: '',
    footer_contact_email: '',
    footer_address: '',
    support_phone: '',
    support_email: ''
  });

  const queryClient = useQueryClient();
  const logoFields = new Set(['logo_url', 'footer_logo_url']);

  const hasTransparentPixels = async (file) => {
    if (typeof window === 'undefined') return false;

    let bitmap = null;
    let objectUrl = '';

    try {
      let width = 0;
      let height = 0;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return false;

      if (typeof createImageBitmap === 'function') {
        bitmap = await createImageBitmap(file);
        width = bitmap.width;
        height = bitmap.height;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(bitmap, 0, 0);
      } else {
        objectUrl = URL.createObjectURL(file);
        const img = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = objectUrl;
        });
        width = img.naturalWidth;
        height = img.naturalHeight;
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0);
      }

      const pixels = ctx.getImageData(0, 0, width, height).data;
      const totalPixels = pixels.length / 4;
      const stride = totalPixels > 50000 ? Math.floor(totalPixels / 50000) : 1;

      for (let i = 3; i < pixels.length; i += 4 * stride) {
        if (pixels[i] < 255) return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      if (bitmap && typeof bitmap.close === 'function') {
        bitmap.close();
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  };

  const validateTransparentLogo = async (file) => {
    const allowedTypes = new Set(['image/png', 'image/webp']);
    if (!allowedTypes.has(file.type)) {
      return {
        ok: false,
        message: 'Para logo transparente, envie PNG ou WebP.'
      };
    }

    const hasTransparency = await hasTransparentPixels(file);
    if (!hasTransparency) {
      return {
        ok: false,
        message: 'A imagem da logo precisa ter fundo transparente.'
      };
    }

    return { ok: true, message: '' };
  };

  const { data: churchInfo, isLoading } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    }
  });

  useEffect(() => {
    if (churchInfo) {
      setFormData({
        name: churchInfo.name || '',
        logo_url: churchInfo.logo_url || '',
        history: churchInfo.history || '',
        mission: churchInfo.mission || '',
        vision: churchInfo.vision || '',
        values: churchInfo.values || '',
        cover_image_url: churchInfo.cover_image_url || '',
        address: churchInfo.address || '',
        phone: churchInfo.phone || '',
        whatsapp: churchInfo.whatsapp || '',
        email: churchInfo.email || '',
        website: churchInfo.website || '',
        instagram: churchInfo.instagram || '',
        facebook: churchInfo.facebook || '',
        youtube_channel: churchInfo.youtube_channel || '',
        youtube_live_url: churchInfo.youtube_live_url || '',
        youtube_videos_feed_url: churchInfo.youtube_videos_feed_url || '',
        latitude: churchInfo.latitude || '',
        longitude: churchInfo.longitude || '',
        donation_title: churchInfo.donation_title || '',
        donation_text: churchInfo.donation_text || '',
        donation_image_url: churchInfo.donation_image_url || '',
        pix_qr_image_url: churchInfo.pix_qr_image_url || '',
        pix_key: churchInfo.pix_key || '',
        pix_copy_link: churchInfo.pix_copy_link || '',
        bank_name: churchInfo.bank_name || '',
        bank_agency: churchInfo.bank_agency || '',
        bank_account: churchInfo.bank_account || '',
        bank_account_type: churchInfo.bank_account_type || '',
        bank_beneficiary: churchInfo.bank_beneficiary || '',
        bank_document: churchInfo.bank_document || '',
        footer_logo_url: churchInfo.footer_logo_url || '',
        footer_link_1_label: churchInfo.footer_link_1_label || '',
        footer_link_2_label: churchInfo.footer_link_2_label || '',
        footer_link_3_label: churchInfo.footer_link_3_label || '',
        footer_contact_phone: churchInfo.footer_contact_phone || '',
        footer_contact_email: churchInfo.footer_contact_email || '',
        footer_address: churchInfo.footer_address || '',
        support_phone: churchInfo.support_phone || '',
        support_email: churchInfo.support_email || ''
      });
    }
  }, [churchInfo]);

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (churchInfo?.id) {
        return base44.entities.ChurchInfo.update(churchInfo.id, data);
      } else {
        return base44.entities.ChurchInfo.create(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['churchInfo'] });
      toast.success('Informações salvas com sucesso!');
    }
  });

  const handleImageUpload = async (e, field) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    if (logoFields.has(field)) {
      const validation = await validateTransparentLogo(file);
      if (!validation.ok) {
        toast.error(validation.message);
        input.value = '';
        return;
      }
    }

    setUploading(true);
    try {
      const fileToUpload = field === 'donation_image_url'
        ? await optimizeBannerImage(file)
        : file;
      const { file_url } = await base44.integrations.Core.UploadFile({ file: fileToUpload });
      setFormData(prev => ({ ...prev, [field]: file_url }));
      toast.success('Imagem enviada!');
    } catch (error) {
      toast.error('Erro ao enviar imagem.');
    } finally {
      setUploading(false);
      input.value = '';
    }
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Nome da igreja é obrigatório.');
      return;
    }
    saveMutation.mutate({
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Informações da Igreja" 
            subtitle="Dados e contato"
            showBack={true}
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Basic Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Church className="w-5 h-5 text-purple-600" />
            Informações Básicas
          </h3>

          <div className="space-y-4">
            <div>
              <Label>Nome da Igreja *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da igreja"
              />
            </div>

            <div>
              <Label>Logo da Igreja</Label>
              <p className="text-xs text-gray-500 mt-1">Formato: PNG/WebP com fundo transparente.</p>
              {formData.logo_url ? (
                <div className="relative mt-2 inline-block">
                  <img
                    src={formData.logo_url}
                    alt="Logo"
                    className="w-24 h-24 object-cover rounded-full"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, logo_url: '' })}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed rounded-full cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 'logo_url')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400" />
                </label>
              )}
            </div>

            <div>
              <Label>Imagem de Capa</Label>
              {formData.cover_image_url ? (
                <div className="relative mt-2">
                  <img
                    src={formData.cover_image_url}
                    alt="Capa"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, cover_image_url: '' })}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'cover_image_url')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-sm text-gray-500 mt-1">Enviar imagem</span>
                </label>
              )}
            </div>

            <div>
              <Label>História</Label>
              <Textarea
                value={formData.history}
                onChange={(e) => setFormData({ ...formData, history: e.target.value })}
                placeholder="Conte a história da igreja..."
                rows={4}
              />
            </div>

            <div>
              <Label>Missão</Label>
              <Textarea
                value={formData.mission}
                onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Visão</Label>
              <Textarea
                value={formData.vision}
                onChange={(e) => setFormData({ ...formData, vision: e.target.value })}
                rows={2}
              />
            </div>

            <div>
              <Label>Valores</Label>
              <Textarea
                value={formData.values}
                onChange={(e) => setFormData({ ...formData, values: e.target.value })}
                rows={3}
              />
            </div>
          </div>
        </motion.div>

        {/* Footer Home */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.27 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Rodapé da Home</h3>

          <div className="space-y-4">
            <div>
              <Label>Logo do rodapé (opcional)</Label>
              <p className="text-xs text-gray-500 mt-1">Formato: PNG/WebP com fundo transparente.</p>
              {formData.footer_logo_url ? (
                <div className="relative mt-2 inline-block">
                  <img
                    src={formData.footer_logo_url}
                    alt="Logo rodapé"
                    className="h-14 object-contain rounded"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, footer_logo_url: '' })}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 'footer_logo_url')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-sm text-gray-500 mt-1">Enviar logo</span>
                </label>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Texto do link 1</Label>
                <Input
                  value={formData.footer_link_1_label}
                  onChange={(e) => setFormData({ ...formData, footer_link_1_label: e.target.value })}
                  placeholder="Igreja"
                />
              </div>
              <div>
                <Label>Texto do link 2</Label>
                <Input
                  value={formData.footer_link_2_label}
                  onChange={(e) => setFormData({ ...formData, footer_link_2_label: e.target.value })}
                  placeholder="Doação"
                />
              </div>
              <div>
                <Label>Texto do link 3</Label>
                <Input
                  value={formData.footer_link_3_label}
                  onChange={(e) => setFormData({ ...formData, footer_link_3_label: e.target.value })}
                  placeholder="Ao vivo"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contato - telefone</Label>
                <Input
                  value={formData.footer_contact_phone}
                  onChange={(e) => setFormData({ ...formData, footer_contact_phone: e.target.value })}
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div>
                <Label>Contato - e-mail</Label>
                <Input
                  value={formData.footer_contact_email}
                  onChange={(e) => setFormData({ ...formData, footer_contact_email: e.target.value })}
                  placeholder="contato@igreja.com"
                />
              </div>
            </div>

            <div>
              <Label>Endereço do rodapé</Label>
              <Textarea
                value={formData.footer_address}
                onChange={(e) => setFormData({ ...formData, footer_address: e.target.value })}
                placeholder="Ex.: Av. Sen. Ramos Caiado, 390 - Maracanã..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Suporte - telefone</Label>
                <Input
                  value={formData.support_phone}
                  onChange={(e) => setFormData({ ...formData, support_phone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div>
                <Label>Suporte - e-mail</Label>
                <Input
                  value={formData.support_email}
                  onChange={(e) => setFormData({ ...formData, support_email: e.target.value })}
                  placeholder="suporte@igreja.com"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Doações */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Doações</h3>

          <div className="space-y-4">
            <div>
              <Label>Título da seção</Label>
              <Input
                value={formData.donation_title}
                onChange={(e) => setFormData({ ...formData, donation_title: e.target.value })}
                placeholder="Doações"
              />
            </div>

            <div>
              <Label>Texto da seção</Label>
              <Textarea
                value={formData.donation_text}
                onChange={(e) => setFormData({ ...formData, donation_text: e.target.value })}
                placeholder="Texto que aparece na home e na página de doações"
                rows={3}
              />
            </div>

            <div>
              <Label>Imagem da seção de doações (Home)</Label>
              {formData.donation_image_url ? (
                <div className="relative mt-2">
                  <img
                    src={formData.donation_image_url}
                    alt="Imagem doação"
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, donation_image_url: '' })}
                    className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'donation_image_url')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-sm text-gray-500 mt-1">Enviar imagem</span>
                </label>
              )}
            </div>

            <div>
              <Label>QR Code Pix (imagem)</Label>
              {formData.pix_qr_image_url ? (
                <div className="relative mt-2 inline-block">
                  <img
                    src={formData.pix_qr_image_url}
                    alt="QR Pix"
                    className="w-40 h-40 object-contain rounded-lg border"
                  />
                  <button
                    onClick={() => setFormData({ ...formData, pix_qr_image_url: '' })}
                    className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full text-xs"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <label className="mt-2 flex flex-col items-center justify-center w-40 h-40 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'pix_qr_image_url')}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Enviar QR</span>
                </label>
              )}
            </div>

            <div>
              <Label>Chave Pix</Label>
              <Input
                value={formData.pix_key}
                onChange={(e) => setFormData({ ...formData, pix_key: e.target.value })}
                placeholder="email, telefone, CPF/CNPJ ou chave aleatoria"
              />
            </div>

            <div>
              <Label>Link/Código Pix Copia e Cola</Label>
              <Textarea
                value={formData.pix_copy_link}
                onChange={(e) => setFormData({ ...formData, pix_copy_link: e.target.value })}
                placeholder="Cole aqui o payload/link do Pix para copia rápida"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Banco</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Agência</Label>
                <Input
                  value={formData.bank_agency}
                  onChange={(e) => setFormData({ ...formData, bank_agency: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Conta</Label>
                <Input
                  value={formData.bank_account}
                  onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                />
              </div>
              <div>
                <Label>Tipo de conta</Label>
                <Input
                  value={formData.bank_account_type}
                  onChange={(e) => setFormData({ ...formData, bank_account_type: e.target.value })}
                  placeholder="Corrente / Poupanca"
                />
              </div>
            </div>

            <div>
              <Label>Favorecido</Label>
              <Input
                value={formData.bank_beneficiary}
                onChange={(e) => setFormData({ ...formData, bank_beneficiary: e.target.value })}
              />
            </div>

            <div>
              <Label>CPF/CNPJ do favorecido</Label>
              <Input
                value={formData.bank_document}
                onChange={(e) => setFormData({ ...formData, bank_document: e.target.value })}
              />
            </div>

            <div className="pt-4 border-t border-gray-200 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-gray-700">Preview em tempo real</h4>
                <Link to={createPageUrl('Doacoes')} target="_blank" rel="noreferrer">
                  <Button type="button" variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir página de doações
                  </Button>
                </Link>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 mb-2">Preview Home</p>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
                  <div className="grid md:grid-cols-2">
                    <div className="h-40 bg-gray-100">
                      {formData.donation_image_url ? (
                        <img src={formData.donation_image_url} alt="Preview doação" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-200 to-zinc-300">
                          <Heart className="w-12 h-12 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Heart className="w-6 h-6 text-zinc-700" />
                        <h5 className="text-xl font-bold text-zinc-900">{formData.donation_title || 'Doações'}</h5>
                      </div>
                      <p className="text-sm text-zinc-700 mb-3 line-clamp-3">
                        {formData.donation_text || 'A doação é uma forma de contribuir com os projetos e com a obra de Deus.'}
                      </p>
                      <Button className="w-full h-10 bg-zinc-800 hover:bg-zinc-900 text-white text-sm font-semibold" type="button">
                        Fazer uma doação
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 mb-2">Preview Pagina de Doacao</p>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <QrCode className="w-4 h-4 text-emerald-700" />
                      <p className="font-semibold text-sm">Pix</p>
                    </div>
                    <div className="flex justify-center mb-2">
                      {formData.pix_qr_image_url ? (
                        <img src={formData.pix_qr_image_url} alt="QR Preview" className="w-28 h-28 object-contain border rounded-lg" />
                      ) : (
                        <div className="w-28 h-28 border rounded-lg flex items-center justify-center text-xs text-gray-400">QR Code</div>
                      )}
                    </div>
                    {formData.pix_key && (
                      <>
                        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 mb-1">Chave Pix</Badge>
                        <p className="text-xs break-all">{formData.pix_key}</p>
                      </>
                    )}
                    {formData.pix_copy_link && (
                      <>
                        <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 mt-2 mb-1">Pix Copia e Cola</Badge>
                        <p className="text-xs break-all">{formData.pix_copy_link}</p>
                      </>
                    )}
                  </div>

                  <div className="bg-white rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Landmark className="w-4 h-4 text-indigo-700" />
                      <p className="font-semibold text-sm">Transferência</p>
                    </div>
                    <div className="space-y-1 text-xs text-gray-700">
                      {formData.bank_name && <p><strong>Banco:</strong> {formData.bank_name}</p>}
                      {formData.bank_agency && <p><strong>Agência:</strong> {formData.bank_agency}</p>}
                      {formData.bank_account && <p><strong>Conta:</strong> {formData.bank_account}</p>}
                      {formData.bank_account_type && <p><strong>Tipo:</strong> {formData.bank_account_type}</p>}
                      {formData.bank_beneficiary && <p><strong>Favorecido:</strong> {formData.bank_beneficiary}</p>}
                      {formData.bank_document && <p><strong>CPF/CNPJ:</strong> {formData.bank_document}</p>}
                      {!formData.bank_name && !formData.bank_account && (
                        <p className="text-gray-400">Preencha os dados para visualizar.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Contato</h3>

          <div className="space-y-4">
            <div>
              <Label>Endereço</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Endereço completo"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div>
              <Label>E-mail</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contato@igreja.com"
              />
            </div>

            <div>
              <Label>Site</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.igreja.com"
              />
            </div>
          </div>
        </motion.div>

        {/* Social Networks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Redes Sociais</h3>

          <div className="space-y-4">
            <div>
              <Label>Instagram</Label>
              <Input
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="https://instagram.com/sua-igreja"
              />
            </div>

            <div>
              <Label>Facebook</Label>
              <Input
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                placeholder="https://facebook.com/sua-igreja"
              />
            </div>

            <div>
              <Label>Canal do YouTube</Label>
              <Input
                value={formData.youtube_channel}
                onChange={(e) => setFormData({ ...formData, youtube_channel: e.target.value })}
                placeholder="https://youtube.com/@sua-igreja"
              />
            </div>

            <div>
              <Label>Link da transmissão ao vivo</Label>
              <Input
                value={formData.youtube_live_url}
                onChange={(e) => setFormData({ ...formData, youtube_live_url: e.target.value })}
                placeholder="Cole o link da live ou do vídeo ao vivo"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use o link do vídeo/live do YouTube para exibir o player dentro do app.
              </p>
            </div>

            <div>
              <Label>Feed de vídeos recentes do YouTube</Label>
              <Input
                value={formData.youtube_videos_feed_url}
                onChange={(e) => setFormData({ ...formData, youtube_videos_feed_url: e.target.value })}
                placeholder="https://www.youtube.com/feeds/videos.xml?channel_id=..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Opcional. Use quando o canal estiver como @usuario. Com link /channel/UC..., o app tenta preencher automaticamente.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Location */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-sm p-6"
        >
          <h3 className="font-semibold text-gray-900 mb-4">Localização (Mapa)</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitude</Label>
              <Input
                value={formData.latitude}
                onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                placeholder="-23.550520"
              />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input
                value={formData.longitude}
                onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                placeholder="-46.633308"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Você pode obter as coordenadas no Google Maps.
          </p>
        </motion.div>

        <Button 
          className="w-full" 
          onClick={handleSubmit}
          disabled={saveMutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>
    </div>
  );
}
